import {
  HTMLAttributes,
  MouseEventHandler,
  ReactElement,
  ReactNode,
  WeakValidationMap,
  Ref,
  RefAttributes,
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
} from 'react';
import PropTypes, { Validator } from 'prop-types';
import { forgeClassHelper } from '../utils/classes';
import ForgeIcons from '../Icon/ForgeIcons';
import ProgressIndicator from '../ProgressIndicator';
import { sanitizeUrl } from '@braintree/sanitize-url';
import { IconProps } from '../Icon';
import { ForgeIconComponent } from '@athena/forge-icons/dist/ForgeIconProps';
import forwardRefToProps from '../utils/forwardRefToProps';
import BEMHelper from 'react-bem-helper';

/** A supported button variant string.
 *
 * type ButtonVariant = typeof buttonVariants[number] would be less duplication
 * effort, but is unsupported by react-docgen v5
 * same story with ButtonSize and ButtonType */
export type ButtonVariant = 'primary' | 'secondary' | 'tertiary';
/** The list of button variants supported */
export const buttonVariants: readonly ButtonVariant[] = ['primary', 'secondary', 'tertiary'];

/** A supported button size string */
export type ButtonSize = 'tiny' | 'small' | 'medium' | 'large';
/** The list of supported button sizes */
export const buttonSizes: readonly ButtonSize[] = ['tiny', 'small', 'medium', 'large'];

/** Supported Button size when an icon is present */
export type ButtonSizeWithIcon = 'medium' | 'large';
/** The list of supported button sizes when an icon is present */
export type ButtonSizeWithoutIcon = Exclude<ButtonSize, ButtonSizeWithIcon>;

/** A supported Button type */
export type ButtonType = 'button' | 'submit' | 'reset';
/** The list of supported Button types */
export const buttonTypes: readonly ButtonType[] = ['button', 'submit', 'reset'];

/** Determines if a Button can have an Icon based on its size. */
const canHaveIcon = (size: ButtonSize, variant: ButtonVariant): boolean =>
  variant === 'tertiary' || size === 'medium' || size === 'large';

/** The props that Button destructures for its internal consumption.
 *
 * This is in contrast to ButtonForwardedProps, which are props forwarded to the
 * underlying DOM element.
 */
export interface ButtonDestructuredProps<UseLink extends boolean> {
  /** Aria label for the underlying <a> or <button> */
  'aria-label'?: string;
  /** Content to display inside button before icon and text */
  children?: ReactNode;
  /** Adds a class to the root element of the component */
  className?: string;
  /** Applies the disabled state class and adds `aria-disabled` attribute to the button element */
  disabled?: boolean;
  /** Applies the full width class to make button the full width of its parent */
  fullWidth?: boolean;
  /** Specifies the link destination if `useLink` is true. Cannot be used if `useLink` if false. */
  href?: UseLink extends true ? string : never;
  /**
   * The icon to display inside the button. Only applicable to medium and large
   * button sizes. It is not recommended to use the solid, special icons
   * (Attention, Critical, Info, and Success) within Button
   * It's recommended to supply an icon from the @athena/forge-icons package.
   * It is recommended to use a small icon for a medium button, eg: `SearchSmall`,
   * or a large icon for a large button, eg: `SearchLarge`.
   * It is not recommended to use icons for small or tiny buttons.
   *
   * Deprecated: Supplying an icon by name, I.E. "Search"
   */
  icon?: string | ForgeIconComponent;
  /** The title to apply to the icon. Defaults to empty string */
  iconTitle?: string;
  /** @deprecated Since 5.0.0
   *
   * Specify a custom icon set defined as a dictionary of `IconBase` components.
   * Icon sets can be generated using the [App Fabric Forge Extension Generator](https://bitbucket.athenahealth.com/projects/UXDS/repos/app-fabric-generator-forge-extension/browse).
   */
  iconSet?: Record<string, (props: Omit<IconProps, 'icon' | 'iconSet'>) => ReactElement>;
  /** Handles click event on the button. Type is dependent on the useLink prop. */
  onClick?: UseLink extends true ? MouseEventHandler<HTMLAnchorElement> : MouseEventHandler<HTMLButtonElement>;
  /** Set to true to display a loading spinner to the left of the Button's content. This is only supported for medium and large size buttons. */
  showLoadingSpinner?: boolean;
  /** When specified, this can change button size from medium to tiny, small, or large */
  size?: ButtonSize;
  /** Button text */
  text?: string;
  /** Sets the `type` attribute on the button element (e.g. 'button', 'submit', or 'reset').
   * Will not be used if `useLink` is true. */
  type?: UseLink extends true ? never : ButtonType;
  /** If true, renders the component as a `<a>` element instead of a `<button>` element.
   *
   * Set to true if the click action navigates the user to a new context (e.g., new page or workflow).
   * If true, an `href` must be provided. Defaults to false.
   */
  useLink?: UseLink;
  /** Applies styling based on pre-determined variants.
   *
   * Primary, secondary, and  tertiary respectively convey diminishing emphasis
   * on the button. If a button has an icon with no accompanying text, then the
   * tertiary variant is recommended.
   */
  variant?: ButtonVariant;
}

/** The prop keys consumed directly by the Button component */
export type KeyofButtonProps = keyof ButtonDestructuredProps<boolean>;

/** The props forwarded by Button to its underlying native DOM element.
 *
 * This type is only applied at the last possible second because the keys of
 * this type change depending on UseLink. This confuses Typescript.
 * Instead, ButtonDestructuredProps and ButtonElementDestructuredProps hold the
 * props that we care about, and type assertions to ButtonForwardedProps are
 * done where necessary.
 */
export type ButtonForwardedProps<UseLink extends boolean> = UseLink extends true
  ? Omit<AnchorHTMLAttributes<HTMLAnchorElement>, KeyofButtonProps>
  : Omit<ButtonHTMLAttributes<HTMLButtonElement>, KeyofButtonProps>;

/** The Button Props for the final Button component
 *
 * Contains advanced type checking. Ensures that incompatible props aren't used together.
 */
export type ButtonProps<UseLink extends boolean> = ButtonDestructuredProps<UseLink> & ButtonForwardedProps<UseLink>;

/** The HTML Element rendered by Button.
 *
 * Dependent on UseLink */
export type ButtonHTMLElement<UseLink extends boolean> = UseLink extends true ? HTMLAnchorElement : HTMLButtonElement;

/** The props used to implement Button.
 *
 * forwardedRef is a prop that does not exist on the final Button component.
 * It represents the `ref` prop given by React.forwardRef, as React.forwardRef
 * introduces a ton of extra compile-time and runtime complexity.
 */
export type ButtonComponentProps<UseLink extends boolean> = ButtonProps<UseLink> & {
  forwardedRef?: Ref<ButtonHTMLElement<UseLink>>;
};

/** The generator for Button css classes. */
const classes = forgeClassHelper('button');

/** The default click handler for Button. */
function handleClick<T>(disabled: boolean, onClick?: MouseEventHandler<T>) {
  return (e: React.MouseEvent<T, MouseEvent>): void => {
    if (disabled) {
      e.preventDefault();
      return;
    }

    if (onClick) {
      // Too many levels of indirection have taken place for Typescript to know
      // that e can only the one type or the other, so do a type assertion.
      onClick(e);
    }
  };
}

/** Button props that the implementation for ButtonElement explicitly checks for */
export interface ButtonElementDestructuredProps<UseLink extends boolean> {
  'aria-label'?: string;
  children?: ReactNode;
  className?: string;
  disabled: boolean;
  forwardedRef?: Ref<ButtonHTMLElement<UseLink>>;
  fullWidth: boolean;
  href?: UseLink extends true ? string : never;
  icon?: string | ForgeIconComponent;
  iconOnly: boolean;
  onClick: UseLink extends true ? MouseEventHandler<HTMLAnchorElement> : MouseEventHandler<HTMLButtonElement>;
  size: ButtonSize;
  text?: string;
  type: UseLink extends true ? never : ButtonType;
  useLink: UseLink;
  variant: ButtonVariant;
}

/** Props for ButtonElement */
export type ButtonElementProps<UseLink extends boolean> = ButtonElementDestructuredProps<UseLink> &
  ButtonForwardedProps<UseLink>;

/** Renders an <a> or <button> based on the useLink prop. */
function ButtonElement<UseLink extends boolean>({
  'aria-label': ariaLabel,
  children,
  className,
  disabled,
  forwardedRef,
  fullWidth,
  href,
  icon,
  iconOnly,
  onClick,
  size,
  text,
  type,
  useLink,
  variant,
  ...passthroughProps
}: ButtonElementProps<UseLink>): ReactElement {
  const commonClassName = classes({
    states: { disabled: !!disabled },
    modifiers: {
      [variant]: !!variant,
      'full-width': fullWidth,
      [size]: true,
      'icon-only': iconOnly,
    },
    extra: className,
  });
  const iconAriaLabel = typeof icon === 'function' ? icon.title : icon;
  const commonProps = {
    'aria-disabled': disabled,
    'aria-label': ariaLabel ?? (canHaveIcon(size, variant) && !text ? iconAriaLabel : undefined),
  };
  if (useLink) {
    const linkHref = href ? { href: sanitizeUrl(href) } : null;
    return (
      <a
        {...(passthroughProps as HTMLAttributes<HTMLAnchorElement>)}
        {...classes({ modifiers: ['link'], extra: commonClassName.className })}
        {...commonProps}
        {...linkHref}
        onClick={handleClick(disabled, onClick as MouseEventHandler<HTMLAnchorElement>)}
        ref={forwardedRef as Ref<HTMLAnchorElement> | undefined}
      >
        {children}
      </a>
    );
  } else {
    return (
      <button
        {...(passthroughProps as HTMLAttributes<HTMLButtonElement>)}
        {...commonClassName}
        {...commonProps}
        type={type}
        onClick={handleClick(disabled, onClick as MouseEventHandler<HTMLButtonElement>)}
        ref={forwardedRef as Ref<HTMLButtonElement>}
      >
        {/** button cannot have flex styling, so use an inner div */}
        <div {...classes({ element: 'content', modifiers: size ? [size] : [] })}>{children}</div>
      </button>
    );
  }
}

/** Chooses an appropriate title for the icon
 *
 * If a title is not explicitly set then it's only appropriate to have a title
 * if there is no accompanying text. In that case, the icon will use its default
 * title.
 */
const pickIconTitle = ({ iconTitle, text }: { iconTitle?: string; text?: string }): string | undefined => {
  if (iconTitle === undefined) {
    // Empty string if there is accompanying text. Otherwise, `undefined` will
    // trigger the icon component to use its default title.
    return text ? '' : undefined;
  } else {
    return iconTitle;
  }
};

/** Props for ButtonIcon */
export type ButtonIconProps = Pick<ButtonComponentProps<boolean>, 'disabled' | 'text' | 'icon' | 'iconTitle'> &
  Required<Pick<ButtonComponentProps<boolean>, 'size' | 'iconSet' | 'variant'>>;

/** Renders an Icon within the Button */
const ButtonIcon = ({ disabled, icon, iconTitle, iconSet, size, text, variant }: ButtonIconProps): ReactElement => {
  const iconIsNeutral = typeof icon === 'function' ? icon.isNeutralInteractive : false;
  const commonClassesArgs: { states: BEMHelper.WordSet; modifiers: BEMHelper.WordSet } = {
    states: { disabled: !!disabled },
    modifiers: { [variant]: true, neutral: !!iconIsNeutral },
  };
  const title = pickIconTitle({ iconTitle, text });
  if (canHaveIcon(size, variant)) {
    if (typeof icon === 'string') {
      if (icon && iconSet[icon]) {
        const IconComponentImpl = iconSet[icon];
        return (
          <IconComponentImpl
            title={title}
            disabled={disabled}
            {...classes({
              element: 'icon',
              ...commonClassesArgs,
            })}
          />
        );
      } else {
        return <></>;
      }
    } else if (typeof icon === 'function') {
      const ForgeIcon = icon;

      return (
        <ForgeIcon
          title={title}
          // Variant is selected via CSS as we need to recolor on hover/focus/active
          {...classes({
            element: 'forgeIcon',
            ...commonClassesArgs,
          })}
        />
      );
    }
  }
  return <></>;
};

/**
 * BUTTON
 *
 * A component to make actions visible and concrete.
 * This will either render a <button> element or an <a> element, with either button or link styling.
 */
function ButtonComponent<UseLink extends boolean>({
  'aria-label': ariaLabel,
  children,
  className,
  disabled = false,
  fullWidth = false,
  forwardedRef,
  href,
  icon,
  iconTitle,
  iconSet = ForgeIcons,
  onClick = () => {
    return;
  },
  showLoadingSpinner = false,
  size = 'medium',
  text,
  type = 'button' as UseLink extends true ? never : ButtonType,
  /* The following type assertion is necessary because clients of Button can
   * technically supply <ButtonComponent<true> /> without providing a useLink
   * prop in runtime. Thus the compile-time types and runtime values will be
   * out of sync.
   */
  useLink = false as UseLink,
  variant = 'primary',
  ...propsForRef
}: ButtonComponentProps<UseLink>): ReactElement {
  /** Typescript gets really confused by ButtonForwardedProps because
   * its keys change depending on the value of UseLink.
   *
   * Type assert in order to keep the peace. */
  const buttonForwardedProps = propsForRef as JSX.IntrinsicAttributes & ButtonForwardedProps<UseLink>;
  return (
    <ButtonElement<UseLink>
      {...buttonForwardedProps}
      aria-label={ariaLabel}
      className={className}
      disabled={disabled}
      forwardedRef={forwardedRef}
      fullWidth={fullWidth}
      href={href}
      icon={icon}
      iconOnly={!text && !children}
      onClick={onClick}
      size={size}
      text={text}
      type={type}
      useLink={useLink}
      variant={variant}
    >
      {showLoadingSpinner && (
        <ProgressIndicator
          {...classes({
            element: 'loading-spinner',
            modifiers: { large: size === 'large' },
            extra: 'fe_u_margin--right-large',
          })}
          shape="circular"
          size="small"
        />
      )}
      {children}
      <ButtonIcon
        disabled={disabled}
        icon={icon}
        iconTitle={iconTitle}
        iconSet={iconSet}
        size={size}
        text={text}
        variant={variant}
      />
      {text && (
        <span
          {...classes({
            element: 'text',
            modifiers: [...(useLink ? ['link'] : []), size, variant],
          })}
        >
          {text}
        </span>
      )}
    </ButtonElement>
  );
}
ButtonComponent.displayName = 'Button';

/** The type for the Button default export.
 *
 * Button is a generic component with ref forwarding, so type assertion is
 * necessary after React.forwardRef is done.
 */
export type ButtonDefaultExport = {
  <UseLink extends boolean = false>(
    props: ButtonProps<UseLink> & RefAttributes<ButtonHTMLElement<UseLink>>
  ): ReturnType<typeof ButtonComponent>;
  supportedProps?: Array<keyof ButtonProps<boolean>>;
};

/** Custom validator for the 'icon' prop */
const iconPropType: Validator<string | undefined> = (props, propName, componentName) => {
  const { size: sizeProp = 'medium', variant } = props as { size?: ButtonSize; variant?: ButtonVariant };
  const icon = props[propName as keyof ButtonProps<boolean>];

  if (
    // icon prop is supplied
    icon !== undefined &&
    // Tertiary buttons allow icons at any size
    variant !== 'tertiary' &&
    // An icon is supplied to button too small to hold the icon.
    sizeProp !== 'medium' &&
    sizeProp !== 'large'
  ) {
    return new Error(
      `Invalid combination of prop
       ${propName}, variant and size supplied to ${componentName}.
       Icon can only be used with medium or large size for primary and secondary variants.`
    );
  } else if (icon !== undefined && typeof icon !== 'string' && typeof icon !== 'function') {
    return new Error(
      `Invalid prop ${propName} supplied to ${componentName}.
      Should be a component defined by @athena/forge-icons.
      Strings are also allowed, but deprecated`
    );
  }
  return null;
};

/** Custom validator for the 'href' prop */
const hrefPropType: Validator<string | undefined> = (props, propName, componentName) => {
  const { useLink } = props as ButtonProps<boolean>;
  if (
    useLink &&
    props[propName as keyof ButtonProps<boolean>] &&
    typeof props[propName as keyof ButtonProps<boolean>] === 'string'
  ) {
    return null;
  } else if (useLink && !props[propName as keyof ButtonProps<boolean>]) {
    return new Error(
      `Invalid combination of prop ${propName} and useLink supplied to ${componentName}. If useLink is true, an href must be provided`
    );
  } else if (useLink && props[propName as keyof ButtonProps<boolean>] !== 'string') {
    return new Error(`Invalid ${propName} supplied to ${componentName}. Must be a string.`);
  } else if (!useLink && props[propName as keyof ButtonProps<boolean>]) {
    return new Error(
      `Invalid combination of prop ${propName} and useLink supplied to ${componentName}. If useLink is false, an href cannot be provided`
    );
  }
  return null;
};

/** Custom validator for the 'text' prop */
const textPropType: Validator<string | undefined> = (props, propName, componentName) => {
  if (
    props[propName as keyof ButtonProps<boolean>] &&
    typeof props[propName as keyof ButtonProps<boolean>] !== 'string'
  ) {
    return new Error(`Invalid ${propName} supplied to ${componentName}. Must be a string.`);
  }
  return null;
};

/** Custom validator for the 'showLoadingSpinner' prop */
const showLoadingSpinnerPropType: Validator<boolean | undefined> = (props, propName, componentName) => {
  const { showLoadingSpinner, size } = props;
  if (typeof showLoadingSpinner !== 'undefined' && typeof showLoadingSpinner !== 'boolean') {
    return new Error(`Invalid ${propName} supplied to ${componentName}. Must be a boolean.`);
  } else if (showLoadingSpinner && !(size === undefined || size === 'medium' || size === 'large')) {
    return new Error(
      `Invalid ${propName} supplied to ${componentName}. The loading spinner is only supported on medium and large buttons.`
    );
  }
  return null;
};

/** Runtime prop-types for Button */
const buttonPropTypes: WeakValidationMap<ButtonComponentProps<boolean>> = {
  /** Aria label for the underlying <a> or <button> */
  'aria-label': PropTypes.string,
  /** Content to display inside button before icon and text */
  children: PropTypes.node,
  /** Adds a class to the root element of the component */
  className: PropTypes.string,
  /** Applies the disabled state class and adds `aria-disabled` attribute to the button element */
  disabled: PropTypes.bool,
  /** Applies the full width class to make button the full width of its parent */
  fullWidth: PropTypes.bool,
  /** Specifies the link destination if `useLink` is true. Cannot be used if `useLink` if false. */
  href: hrefPropType,
  /**
   * The icon to display inside the button. Only applicable to medium and large
   * button sizes. It is not recommended to use the solid, special icons
   * (Attention, Critical, Info, and Success) within Button
   * It's recommended to supply an icon from the @athena/forge-icons package.
   * It is recommended to use a small icon for a medium button, eg: `SearchSmall`,
   * or a large icon for a large button, eg: `SearchLarge`.
   * It is not recommended to use icons for small or tiny buttons.
   *
   * Deprecated: Supplying an icon by name, I.E. "Search".
   */
  icon: iconPropType,
  /** The title to apply to the icon. Defaults to empty string */
  iconTitle: PropTypes.string,
  /** Deprecated since 5.0.0
   *
   * Specify a custom icon set defined as a dictionary of `IconBase` components.
   * Icon sets can be generated using the [App Fabric Forge Extension Generator](https://bitbucket.athenahealth.com/projects/UXDS/repos/app-fabric-generator-forge-extension/browse).
   */
  iconSet: PropTypes.objectOf(PropTypes.func.isRequired),
  /** Handles click event on the button. Type is dependent on the useLink prop. */
  onClick: PropTypes.func,
  /** Set to true to display a loading spinner to the left of the Button's content. This is only supported for medium and large size buttons. */
  showLoadingSpinner: showLoadingSpinnerPropType,
  /** When specified, this can change button size from medium to tiny, small, or large */
  size: PropTypes.oneOf(buttonSizes),
  /** Button text */
  text: textPropType,
  /** Sets the `type` attribute on the button element (e.g. 'button', 'submit', or 'reset').
   * Will not be used if `useLink` is true. */
  type: PropTypes.oneOf(buttonTypes) as Validator<ButtonType>,
  /**
   * If true, renders the component as a `<a>` element instead of a `<button>` element.
   *
   * Set to true if the click action navigates the user to a new context (e.g., new page or workflow).
   * If true, an `href` must be provided. Defaults to false.
   */
  useLink: PropTypes.bool,
  /** Applies styling based on pre-determined variants.
   *
   * Primary, secondary, and  tertiary respectively convey diminishing emphasis
   * on the button. If a button has an icon with no accompanying text, then the
   * tertiary variant is recommended.
   */
  variant: PropTypes.oneOf(buttonVariants),
};
ButtonComponent.propTypes = buttonPropTypes;

const Button = forwardRefToProps(ButtonComponent) as ButtonDefaultExport;
Button.supportedProps = ['size'];
export default Button;
